import { EnhancedDataTable } from "adizen-tanstack-table";
import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Eye, MoreHorizontal, FileText, Users, BookOpen } from "lucide-react";
import { authStore } from "@/stores/authStore";
import { permissionStore, PermissionAction, ResourceType } from "@/stores/permissionStore";
import { useSnapshot } from "valtio";
import { getTestStats } from "@/api/testAnalysis";
import { getApiErrorMessage } from "@/utils/apiErrorMessage";

export default function TestAnalytics() {
  const snap = useSnapshot(authStore);
  const permSnap = useSnapshot(permissionStore);
  const navigate = useNavigate();
  const isAdmin =
    snap?.user?.role === "ADMIN" || snap?.user?.role === "SUPER_ADMIN";
  
  // Permission checks
  const canReadAnalytics = permSnap.can(PermissionAction.READ, ResourceType.ANALYTICS);
  const canExportAnalytics = permSnap.can(PermissionAction.EXPORT, ResourceType.ANALYTICS);

  // Must be referentially stable: the table's internal fetch effect keys off
  // this callback's identity, so a new function per render causes duplicate
  // requests.
  const fetchTestStats = useCallback(async (params) => {
    try {
      const {
        page = 1,
        limit = 10,
        search,
        sortBy,
        sortOrder,
        status,
        testType,
        difficulty,
        passed,
        dateFrom,
        dateTo,
        percentage,
        attemptNumber,
        testTitle,
        courseName,
        materialTitle,
        userEmail,
        userMobileNumber,
        indianCdc,
        needsManualGrade,
        ...otherParams
      } = params || {};

      // Build filters object for API
      const filters = {
        page,
        limit,
        sortBy: sortBy || "startedAt",
        sortOrder: sortOrder || "desc",
      };

      // Add search if provided
      if (search) {
        filters.search = search;
      }

      // Handle status filter
      if (status && status !== "all") {
        filters.status = status;
      }

      // Handle test type filter
      if (testType && testType !== "all") {
        filters.testType = testType;
      }

      if (percentage !== undefined && percentage !== null && percentage !== "") {
        filters.percentageMin = percentage;
        filters.percentageMax = percentage;
      }

      // Handle difficulty filter
      if (difficulty && difficulty !== "all") {
        filters.difficulty = difficulty;
      }

      // Handle passed filter
      if (passed && passed !== "all") {
        filters.passed = passed === "true";
      }

      // Handle needsManualGrade filter
      if (needsManualGrade && needsManualGrade !== "all") {
        filters.needsManualGrade = needsManualGrade === "true";
      }

      // Handle date range filters
      if (dateFrom) {
        filters.dateFrom = dateFrom;
      }
      if (dateTo) {
        filters.dateTo = dateTo;
      }

      if (attemptNumber) {
        filters.attemptNumber = parseInt(attemptNumber);
      }

      // Handle specific filters
      if (testTitle) filters.testTitle = testTitle;
      if (courseName) filters.courseName = courseName;
      if (materialTitle) filters.materialTitle = materialTitle;
      if (userEmail) filters.userEmail = userEmail;
      // Backend query param is `cdc`
      if (indianCdc) filters.cdc = indianCdc;

      // Add any other parameters that weren't explicitly handled
      Object.keys(otherParams).forEach((key) => {
        if (otherParams[key] && otherParams[key] !== "all") {
          filters[key] = otherParams[key];
        }
      });

      if (!isAdmin) {
        // Keep only the CDC and basic pagination/sort keys for seafarers
        const allowedKeys = ["page", "limit", "sortBy", "sortOrder", "cdc"];
        Object.keys(filters).forEach((key) => {
          if (!allowedKeys.includes(key)) {
            delete filters[key];
          }
        });
      }

      const response = await getTestStats(filters);
      if (response.success && Array.isArray(response.data.stats)) {
        const mappedStats = response.data.stats.map((stat) => ({
          id: stat.id,
          userId: stat.userId,
          testId: stat.testId,
          attemptNumber: stat.attemptNumber,
          status: stat.status,
          startedAt: stat.startedAt,
          completedAt: stat.completedAt,
          timeSpent: stat.timeSpent,
          timeRemaining: stat.timeRemaining,
          totalQuestions: stat.totalQuestions,
          answeredQuestions: stat.answeredQuestions,
          correctAnswers: stat.correctAnswers,
          incorrectAnswers: stat.incorrectAnswers,
          skippedQuestions: stat.skippedQuestions,
          rawScore: stat.rawScore,
          maxScore: stat.maxScore,
          percentage: stat.percentage,
          passed: stat.passed,
          needsManualGrade: stat.needsManualGrade,
          createdAt: stat.createdAt,
          updatedAt: stat.updatedAt,
          date: stat.date,
          testsAttempted: stat.testsAttempted,
          testsAnsweredCorrectly: stat.testsAnsweredCorrectly,
          testsAnsweredIncorrectly: stat.testsAnsweredIncorrectly,

          // User information - ONLY name, email, mobile, CDC
          userName: stat.user?.name,
          userEmail: stat.user?.email,
          userCdc: stat.user?.indianCdc,

          // Test information
          testTitle: stat.test?.title,
          testType: stat.test?.type,
          testDifficulty: stat.test?.difficulty,
          testTimeLimit: stat.test?.timeLimit,
          testMaxAttempts: stat.test?.maxAttempts,
          testPassingScore: stat.test?.passingScore,
          testIsActive: stat.test?.isActive,

          // Material information (if available)
          materialTitle: stat.test?.learningMaterial?.title,
          materialTopicName: stat.test?.learningMaterial?.topic?.name,
          materialCourseName: stat.test?.learningMaterial?.topic?.course?.name,

          // Course information (if test is course-level)
          courseName: stat.test?.course?.name,

          // Raw data for actions
          _raw: stat,
        }));

        return {
          success: true,
          message: response.message,
          data: mappedStats,
          meta: {
            total: response.data.pagination?.total || mappedStats.length,
            page: response.data.pagination?.page || page,
            limit: response.data.pagination?.limit || limit,
            totalPages:
              response.data.pagination?.totalPages ||
              Math.ceil(mappedStats.length / limit),
            hasNext: response.data.pagination?.hasNext || false,
            hasPrev: response.data.pagination?.hasPrev || false,
          },
        };
      } else {
        throw new Error(response.message || "Failed to fetch test statistics");
      }
    } catch (error) {
      console.error("Error fetching test stats:", error);
      toast.error(getApiErrorMessage(error, "Failed to fetch test statistics"));
      return {
        success: false,
        message: error.message,
        data: [],
        meta: {
          total: 0,
          page: 1,
          limit: 10,
          totalPages: 0,
          hasNext: false,
          hasPrev: false,
        },
      };
    }
  }, [isAdmin]);

  // Built here rather than via useServerSideTableData: that hook returns a new
  // config object (and a new onDataRequest closure) on every render, which
  // re-triggers the table's internal fetch effect and produces duplicate
  // requests. A memoised config keeps the identity stable.
  const tableServerSide = useMemo(
    () => ({ enabled: true, onDataRequest: fetchTestStats, debounceMs: 500 }),
    [fetchTestStats]
  );

  // Handle actions
  const handleViewTest = (testId) => {
    navigate(`/tests/${testId}`);
  };

  const handleViewUser = (userId) => {
    navigate(`/admin/users/view/${userId}`);
  };

  const handleViewAttempt = (attemptId) => {
    navigate(`/test-attempts/${attemptId}`);
  };

  const columns = useMemo(() => {
    const usercols = [
      {
        id: "userName",
        header: "User Name",
        accessorKey: "userName",
        accessorFn: (stat) => (
          isAdmin ? <span className="text-nowrap">{stat.userName}</span> : ""
        ),
        enableGlobalFilter: true,
        enableColumnFilter: true,
        filterType: "text",
        serverFilterKey: "search",
        serverSortKey: "userName",
        size: 150,
        exportConfig: {
          exportHeader: "User Name",
          exportValueFn: (stat) => stat.userName,
        },
      },
      {
        id: "userEmail",
        header: "User Email",
        accessorKey: "userEmail",
        accessorFn: (stat) => (
          <span className="text-nowrap">{stat.userEmail}</span>
        ),
        enableGlobalFilter: true,
        enableColumnFilter: true,
        filterType: "text",
        serverFilterKey: "search",
        serverSortKey: "userEmail",
        size: 200,
        exportConfig: {
          exportHeader: "User Email",
          exportValueFn: (stat) => stat.userEmail,
        },
      },
      {
        id: "userMobile",
        header: "User Mobile",
        accessorKey: "userMobile",
        accessorFn: (stat) => (
          <span className="text-nowrap">{stat.userMobile}</span>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: true,
        filterType: "text",
        serverFilterKey: "search",
        serverSortKey: "userMobile",
        size: 120,
        exportConfig: {
          exportHeader: "User Mobile",
          exportValueFn: (stat) => stat.userMobile,
        },
      },
    ];

    const testcols = [
      {
        id: "testTitle",
        header: "Test Title",
        accessorKey: "testTitle",
        accessorFn: (stat) => (
          <span className="text-nowrap">{stat.testTitle}</span>
        ),
        enableGlobalFilter: true,
        enableColumnFilter: true,
        filterType: "text",
        serverFilterKey: "search",
        serverSortKey: "testTitle",
        size: 200,
        exportConfig: {
          exportHeader: "Test Title",
          exportValueFn: (stat) => stat.testTitle,
        },
      },
      {
        id: "testType",
        header: "Test Type",
        accessorKey: "testType",
        accessorFn: (stat) => (
          <span className="text-nowrap">{stat.testType}</span>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: true,
        filterType: "select",
        filterOptions: [
          { label: "Quiz", value: "QUIZ" },
          { label: "Assessment", value: "ASSESSMENT" },
        ],
        serverFilterKey: "testType",
        serverSortKey: "testType",
        size: 100,
        cell: ({ getValue }) => {
          const type = getValue();
          return (
            <Badge
              className={
                type === "QUIZ"
                  ? "bg-blue-100 text-blue-800"
                  : "bg-purple-100 text-purple-800"
              }
            >
              {type}
            </Badge>
          );
        },
        exportConfig: {
          exportHeader: "Test Type",
          exportValueFn: (stat) => stat.testType,
        },
      },
      {
        id: "testDifficulty",
        header: "Difficulty",
        accessorKey: "testDifficulty",
        accessorFn: (stat) => (
          <span className="text-nowrap">{stat.testDifficulty}</span>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: true,
        filterType: "select",
        filterOptions: [
          { label: "Beginner", value: "BEGINNER" },
          { label: "Intermediate", value: "INTERMEDIATE" },
          { label: "Advanced", value: "ADVANCED" },
        ],
        serverFilterKey: "difficulty",
        serverSortKey: "testDifficulty",
        size: 120,
        cell: ({ getValue }) => {
          const difficulty = getValue();
          let color = "bg-gray-100 text-gray-800";
          if (difficulty === "BEGINNER") color = "bg-green-100 text-green-800";
          else if (difficulty === "INTERMEDIATE")
            color = "bg-yellow-100 text-yellow-800";
          else if (difficulty === "ADVANCED") color = "bg-red-100 text-red-800";

          return <Badge className={color}>{difficulty}</Badge>;
        },
        exportConfig: {
          exportHeader: "Difficulty",
          exportValueFn: (stat) => stat.testDifficulty,
        },
      },
      {
        id: "status",
        header: "Status",
        accessorKey: "status",
        accessorFn: (stat) => stat.status,
        enableGlobalFilter: false,
        enableColumnFilter: true,
        filterType: "select",
        filterOptions: [
          { label: "Started", value: "STARTED" },
          { label: "In Progress", value: "IN_PROGRESS" },
          { label: "Completed", value: "COMPLETED" },
          { label: "Abandoned", value: "ABANDONED" },
          { label: "Expired", value: "EXPIRED" },
        ],
        serverFilterKey: "status",
        serverSortKey: "status",
        size: 120,
        cell: ({ getValue }) => {
          const status = getValue();
          let color = "bg-gray-100 text-gray-800";
          if (status === "COMPLETED") color = "bg-green-100 text-green-800";
          else if (status === "IN_PROGRESS")
            color = "bg-blue-100 text-blue-800";
          else if (status === "STARTED")
            color = "bg-yellow-100 text-yellow-800";
          else if (status === "ABANDONED" || status === "EXPIRED")
            color = "bg-red-100 text-red-800";

          return (
            <Badge className={color + " text-nowrap"}>
              {status.replace("_", " ")}
            </Badge>
          );
        },
        exportConfig: {
          exportHeader: "Status",
          exportValueFn: (stat) => stat.status,
        },
      },
      {
        id: "passed",
        header: "Passed",
        accessorKey: "passed",
        // Must return the boolean: a <span> is always truthy, so every
        // attempt showed "YES".
        accessorFn: (stat) => stat.passed,
        enableGlobalFilter: false,
        enableColumnFilter: true,
        filterType: "select",
        filterOptions: [
          { label: "Yes", value: "true" },
          { label: "No", value: "false" },
        ],
        serverFilterKey: "passed",
        serverSortKey: "passed",
        size: 100,
        cell: ({ getValue }) => {
          const passed = getValue();
          return (
            <Badge
              className={
                passed
                  ? "bg-green-100 text-green-800"
                  : "bg-red-100 text-red-800"
              }
            >
              {passed ? "YES" : "NO"}
            </Badge>
          );
        },
        exportConfig: {
          exportHeader: "Passed",
          exportValueFn: (stat) => (stat.passed ? "Yes" : "No"),
        },
      },
      {
        id: "testTimeLimit",
        header: "Time Limit",
        accessorKey: "testTimeLimit",
        accessorFn: (stat) => (
          <span className="text-nowrap">{stat.testTimeLimit}</span>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "number",
        serverFilterKey: "testTimeLimit",
        serverSortKey: "testTimeLimit",
        size: 100,
        cell: ({ getValue }) => {
          const minutes = getValue();
          return <span>{minutes} min</span>;
        },
        exportConfig: {
          exportHeader: "Time Limit (minutes)",
          exportValueFn: (stat) => stat.testTimeLimit,
        },
      },
      {
        id: "testMaxAttempts",
        header: "Max Attempts",
        accessorKey: "testMaxAttempts",
        accessorFn: (stat) => (
          <span className="text-nowrap">{stat.testMaxAttempts}</span>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "number",
        serverFilterKey: "testMaxAttempts",
        serverSortKey: "testMaxAttempts",
        size: 120,
        exportConfig: {
          exportHeader: "Max Attempts",
          exportValueFn: (stat) => stat.testMaxAttempts,
        },
      },
      {
        id: "testPassingScore",
        header: "Passing Score",
        accessorKey: "testPassingScore",
        accessorFn: (stat) => (
          <span className="text-nowrap">{stat.testPassingScore}</span>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "number",
        serverFilterKey: "percentage",
        serverSortKey: "testPassingScore",
        size: 120,
        cell: ({ getValue }) => {
          const score = getValue();
          return <span>{score}%</span>;
        },
        exportConfig: {
          exportHeader: "Passing Score",
          exportValueFn: (stat) => stat.testPassingScore,
        },
      },
      {
        id: "testIsActive",
        header: "Test Active",
        accessorKey: "testIsActive",
        accessorFn: (stat) => (
          <span className="text-nowrap">{stat.testIsActive}</span>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "selectsearch",
        filterOptions: [
          { label: "Active", value: "true" },
          { label: "Inactive", value: "false" },
        ],
        serverFilterKey: "testIsActive",
        serverSortKey: "testIsActive",
        size: 100,
        cell: ({ getValue }) => {
          const isActive = getValue();
          return (
            <Badge
              className={
                isActive
                  ? "bg-green-100 text-green-800"
                  : "bg-gray-100 text-gray-800"
              }
            >
              {isActive ? "YES" : "NO"}
            </Badge>
          );
        },
        exportConfig: {
          exportHeader: "Test Active",
          exportValueFn: (stat) => (stat.testIsActive ? "Yes" : "No"),
        },
      },
      {
        id: "materialTitle",
        header: "Material Title",
        accessorKey: "materialTitle",
        accessorFn: (stat) => (
          <span className="text-nowrap">{stat.materialTitle}</span>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "text",
        serverFilterKey: "search",
        serverSortKey: "materialTitle",
        size: 200,
        exportConfig: {
          exportHeader: "Material Title",
          exportValueFn: (stat) => stat.materialTitle,
        },
      },
      {
        id: "materialTopicName",
        header: "Topic Name",
        accessorKey: "materialTopicName",
        accessorFn: (stat) => (
          <span className="text-nowrap">{stat.materialTopicName}</span>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "text",
        serverFilterKey: "search",
        serverSortKey: "materialTopicName",
        size: 150,
        exportConfig: {
          exportHeader: "Topic Name",
          exportValueFn: (stat) => stat.materialTopicName,
        },
      },
      {
        id: "materialCourseName",
        header: "Material Course",
        accessorKey: "materialCourseName",
        accessorFn: (stat) => (
          <span className="text-nowrap">{stat.materialCourseName}</span>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "text",
        serverFilterKey: "search",
        serverSortKey: "materialCourseName",
        size: 150,
        exportConfig: {
          exportHeader: "Material Course",
          exportValueFn: (stat) => stat.materialCourseName,
        },
      },
      {
        id: "courseName",
        header: "Course Name",
        accessorKey: "courseName",
        accessorFn: (stat) => (
          <span className="text-nowrap">{stat.courseName}</span>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "text",
        serverFilterKey: "search",
        serverSortKey: "courseName",
        size: 150,
        exportConfig: {
          exportHeader: "Course Name",
          exportValueFn: (stat) => stat.courseName,
        },
      },
      {
        id: "attemptNumber",
        header: "Attempt Number",
        accessorKey: "attemptNumber",
        accessorFn: (stat) => (
          <span className="text-nowrap">{stat.attemptNumber}</span>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: false,
        enableSorting: true,
        filterType: "number",
        serverFilterKey: "attemptNumber",
        serverSortKey: "attemptNumber",
        size: 50,
        exportConfig: {
          exportHeader: "Attempt Number",
          exportValueFn: (stat) => stat.attemptNumber,
        },
      },
      {
        id: "timeSpent",
        header: "Time Spent",
        accessorKey: "timeSpent",
        accessorFn: (stat) => (
          <span className="text-nowrap">{stat.timeSpent}</span>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "number",
        serverFilterKey: "timeSpent",
        serverSortKey: "timeSpent",
        size: 100,
        exportConfig: {
          exportHeader: "Time Spent",
          exportValueFn: (stat) => stat.timeSpent,
        },
      },
      {
        id: "timeRemaining",
        header: "Time Remaining",
        accessorKey: "timeRemaining",
        accessorFn: (stat) => (
          <span className="text-nowrap">{stat.timeRemaining}</span>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "number",
        serverFilterKey: "timeRemaining",
        serverSortKey: "timeRemaining",
        size: 130,
        exportConfig: {
          exportHeader: "Time Remaining",
          exportValueFn: (stat) => stat.timeRemaining,
        },
      },
      {
        id: "totalQuestions",
        header: "Total Questions",
        accessorKey: "totalQuestions",
        accessorFn: (stat) => (
          <span className="text-nowrap">{stat.totalQuestions}</span>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: false,
        enableSorting: true,
        filterType: "number",
        serverFilterKey: "totalQuestions",
        serverSortKey: "totalQuestions",
        size: 130,
        exportConfig: {
          exportHeader: "Total Questions",
          exportValueFn: (stat) => stat.totalQuestions,
        },
      },
      {
        id: "answeredQuestions",
        header: "Answered Questions",
        accessorKey: "answeredQuestions",
        accessorFn: (stat) => (
          <span className="text-nowrap">{stat.answeredQuestions}</span>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "number",
        serverFilterKey: "answeredQuestions",
        serverSortKey: "answeredQuestions",
        size: 150,
        exportConfig: {
          exportHeader: "Answered Questions",
          exportValueFn: (stat) => stat.answeredQuestions,
        },
      },
      {
        id: "correctAnswers",
        header: "Correct Answers",
        accessorKey: "correctAnswers",
        accessorFn: (stat) => (
          <span className="text-nowrap">{stat.correctAnswers}</span>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: false,
        enableSorting: true,
        filterType: "number",
        serverFilterKey: "correctAnswers",
        serverSortKey: "correctAnswers",
        size: 130,
        exportConfig: {
          exportHeader: "Correct Answers",
          exportValueFn: (stat) => stat.correctAnswers,
        },
      },
      {
        id: "incorrectAnswers",
        header: "Incorrect Answers",
        accessorKey: "incorrectAnswers",
      accessorFn: (stat) => (
          <span className="text-nowrap">{stat.incorrectAnswers}</span>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: false,
        enableSorting: true,
        filterType: "number",
        serverFilterKey: "incorrectAnswers",
        serverSortKey: "incorrectAnswers",
        size: 140,
        exportConfig: {
          exportHeader: "Incorrect Answers",
          exportValueFn: (stat) => stat.incorrectAnswers,
        },
      },
      {
        id: "skippedQuestions",
        header: "Skipped Questions",
        accessorKey: "skippedQuestions",
        accessorFn: (stat) => (
          <span className="text-nowrap">{stat.skippedQuestions}</span>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "number",
        serverFilterKey: "skippedQuestions",
        serverSortKey: "skippedQuestions",
        size: 140,
        exportConfig: {
          exportHeader: "Skipped Questions",
          exportValueFn: (stat) => stat.skippedQuestions,
        },
      },
      {
        id: "rawScore",
        header: "Raw Score",
        accessorKey: "rawScore",
        accessorFn: (stat) => (
          <span className="text-nowrap">{stat.rawScore}</span>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "number",
        serverFilterKey: "rawScore",
        serverSortKey: "rawScore",
        size: 100,
        exportConfig: {
          exportHeader: "Raw Score",
          exportValueFn: (stat) => stat.rawScore,
        },
      },
      {
        id: "maxScore",
        header: "Max Score",
        accessorKey: "maxScore",
        accessorFn: (stat) => (
          <span className="text-nowrap">{stat.maxScore}</span>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: false,
        enableSorting: true,
        filterType: "number",
        serverFilterKey: "maxScore",
        serverSortKey: "maxScore",
        size: 100,
        exportConfig: {
          exportHeader: "Max Score",
          exportValueFn: (stat) => stat.maxScore,
        },
      },
      {
        id: "percentage",
        header: "Percentage",
        accessorKey: "percentage",
        accessorFn: (stat) => (
          <span className="text-nowrap">{typeof stat.percentage === 'number' ? stat.percentage.toFixed(2) : '0.00'}</span>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "number",
        enableSorting: true,
        serverFilterKey: "percentageMin",
        serverSortKey: "percentage",
        size: 120,
        cell: ({ getValue }) => {
          const percentage = getValue();
          return (
            <div className="flex items-center gap-2">
              <span className="font-medium">{percentage}%</span>
              <Progress value={percentage} className="h-2 w-16" />
            </div>
          );
        },
        exportConfig: {
          exportHeader: "Percentage",
          exportValueFn: (stat) => `${stat.percentage}%`,
        },
      },
      {
        id: "testsAnsweredCorrectly",
        header: "Tests Answered Correctly",
        accessorKey: "testsAnsweredCorrectly",
        accessorFn: (stat) => (
          <span className="text-nowrap">{stat.testsAnsweredCorrectly}</span>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "number",
        serverFilterKey: "testsAnsweredCorrectly",
        serverSortKey: "testsAnsweredCorrectly",
        size: 180,
        exportConfig: {
          exportHeader: "Tests Answered Correctly",
          exportValueFn: (stat) => stat.testsAnsweredCorrectly,
        },
      },
      {
        id: "testsAnsweredIncorrectly",
        header: "Tests Answered Incorrectly",
        accessorKey: "testsAnsweredIncorrectly",
        accessorFn: (stat) => (
          <span className="text-nowrap">{stat.testsAnsweredIncorrectly}</span>
        ),
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "number",
        serverFilterKey: "testsAnsweredIncorrectly",
        serverSortKey: "testsAnsweredIncorrectly",
        size: 190,
        exportConfig: {
          exportHeader: "Tests Answered Incorrectly",
          exportValueFn: (stat) => stat.testsAnsweredIncorrectly,
        },
      },
      {
        id: "startedAt",
        header: "Started At",
        accessorKey: "startedAt",
        accessorFn: (stat) => stat.startedAt,
        enableGlobalFilter: false,
        enableColumnFilter: true,
        filterType: "date",
        serverFilterKey: "dateFrom",
        serverSortKey: "startedAt",
        enableSorting: true,
        size: 150,
        cell: ({ getValue }) => {
          const date = getValue();
          return date ? new Date(date).toLocaleString() : "-";
        },
        exportConfig: {
          exportHeader: "Started At",
          exportValueFn: (stat) =>
            stat.startedAt ? new Date(stat.startedAt).toLocaleString() : "-",
        },
      },
      {
        id: "completedAt",
        header: "Completed At",
        accessorKey: "completedAt",
        accessorFn: (stat) => stat.completedAt,
        enableGlobalFilter: false,
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date",
        serverFilterKey: "dateTo",
        serverSortKey: "completedAt",
        size: 150,
        cell: ({ getValue }) => {
          const date = getValue();
          return date ? new Date(date).toLocaleString() : "-";
        },
        exportConfig: {
          exportHeader: "Completed At",
          exportValueFn: (stat) =>
            stat.completedAt
              ? new Date(stat.completedAt).toLocaleString()
              : "-",
        },
      },
      {
        id: "actions",
        header: "Actions",
        accessorKey: "actions",
        enableGlobalFilter: false,
        enableColumnFilter: false,
        enableSorting: false,
        size: 100,
        cell: ({ row }) => {
          const stat = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <span className="sr-only">Open menu</span>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem
                  onClick={() => handleViewUser(stat.userId)}
                  className="cursor-pointer"
                >
                  <Users className="mr-2 h-4 w-4" />
                  <span>View User</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
        exportConfig: {
          excludeFromExport: true,
        },
      },
    ];

    let cols = [
      {
        id: "serialNumber",
        header: "S.No",
        accessorFn: (row, index) => index + 1,
        enableGlobalFilter: false,
        enableColumnFilter: false,
        enableSorting: false,
        size: 60,
      },
    ];
    if(isAdmin) cols.push(...usercols);
    cols.push(...testcols);
    return cols;
  }, [isAdmin]);

  return (
    <div className="space-y-6">
      <div className="min-h-screen overflow-hidden font-sans">
        <div className="w-full flex flex-col gap-4 pr-4">
          <section className="w-full flex flex-col border bg-white p-4 rounded-xl shadow-xl gap-4">
            <div className="flex justify-between items-center">
              <h3 className="uppercase text-lg text-neutral-800 font-bold">
                Test Performance Analytics
              </h3>
            </div>
            <div>
              <EnhancedDataTable
                data={[]}
                columns={columns}
                emptyMessage="No test statistics found"
                enableDateRangeFilter={canReadAnalytics}
                dateFilterField="date"
                enableGlobalSearch={canReadAnalytics}
                globalSearchPlaceholder="Search users, tests, courses..."
                enableColumnVisibility={canReadAnalytics}
                enablePagination={true}
                initialPageSize={10}
                pageSizeOptions={[5, 10, 20, 50, 100]}
                enableCSVExport={canExportAnalytics}
                enableCustomViews={canReadAnalytics}
                viewStorageKey={"test-analytics-view"}
                onViewChange={(view) => {
                  console.log("Test analytics view changed:", view);
                }}
                serverSide={tableServerSide}
                theme="light"
              />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
